import React, { useState, useCallback, useEffect, useMemo } from 'react';

type EmbedCodeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  files: Record<string, string>;
  projectName: string;
};

type EmbedOptions = {
  showEditor: boolean;
  showPreview: boolean;
  showFileTree: boolean;
  theme: 'dark' | 'light' | 'auto';
  height: string;
  width: string;
};

// LZ-String compression for embedding
function compressToEncodedURIComponent(input: string): string {
  if (!input) return '';

  const keyStr = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+-$';

  function compress(uncompressed: string, bitsPerChar: number, getCharFromInt: (a: number) => string): string {
    let i: number;
    let value: number;
    const context_dictionary: Record<string, number> = {};
    const context_dictionaryToCreate: Record<string, boolean> = {};
    let context_c = '';
    let context_wc = '';
    let context_w = '';
    let context_enlargeIn = 2;
    let context_dictSize = 3;
    let context_numBits = 2;
    let context_data: string[] = [];
    let context_data_val = 0;
    let context_data_position = 0;

    for (let ii = 0; ii < uncompressed.length; ii++) {
      context_c = uncompressed.charAt(ii);
      if (!Object.prototype.hasOwnProperty.call(context_dictionary, context_c)) {
        context_dictionary[context_c] = context_dictSize++;
        context_dictionaryToCreate[context_c] = true;
      }

      context_wc = context_w + context_c;
      if (Object.prototype.hasOwnProperty.call(context_dictionary, context_wc)) {
        context_w = context_wc;
      } else {
        if (Object.prototype.hasOwnProperty.call(context_dictionaryToCreate, context_w)) {
          if (context_w.charCodeAt(0) < 256) {
            for (i = 0; i < context_numBits; i++) {
              context_data_val = (context_data_val << 1);
              if (context_data_position === bitsPerChar - 1) {
                context_data_position = 0;
                context_data.push(getCharFromInt(context_data_val));
                context_data_val = 0;
              } else {
                context_data_position++;
              }
            }
            value = context_w.charCodeAt(0);
            for (i = 0; i < 8; i++) {
              context_data_val = (context_data_val << 1) | (value & 1);
              if (context_data_position === bitsPerChar - 1) {
                context_data_position = 0;
                context_data.push(getCharFromInt(context_data_val));
                context_data_val = 0;
              } else {
                context_data_position++;
              }
              value = value >> 1;
            }
          } else {
            value = 1;
            for (i = 0; i < context_numBits; i++) {
              context_data_val = (context_data_val << 1) | value;
              if (context_data_position === bitsPerChar - 1) {
                context_data_position = 0;
                context_data.push(getCharFromInt(context_data_val));
                context_data_val = 0;
              } else {
                context_data_position++;
              }
              value = 0;
            }
            value = context_w.charCodeAt(0);
            for (i = 0; i < 16; i++) {
              context_data_val = (context_data_val << 1) | (value & 1);
              if (context_data_position === bitsPerChar - 1) {
                context_data_position = 0;
                context_data.push(getCharFromInt(context_data_val));
                context_data_val = 0;
              } else {
                context_data_position++;
              }
              value = value >> 1;
            }
          }
          context_enlargeIn--;
          if (context_enlargeIn === 0) {
            context_enlargeIn = Math.pow(2, context_numBits);
            context_numBits++;
          }
          delete context_dictionaryToCreate[context_w];
        } else {
          value = context_dictionary[context_w];
          for (i = 0; i < context_numBits; i++) {
            context_data_val = (context_data_val << 1) | (value & 1);
            if (context_data_position === bitsPerChar - 1) {
              context_data_position = 0;
              context_data.push(getCharFromInt(context_data_val));
              context_data_val = 0;
            } else {
              context_data_position++;
            }
            value = value >> 1;
          }
        }
        context_enlargeIn--;
        if (context_enlargeIn === 0) {
          context_enlargeIn = Math.pow(2, context_numBits);
          context_numBits++;
        }
        context_dictionary[context_wc] = context_dictSize++;
        context_w = String(context_c);
      }
    }

    if (context_w !== '') {
      if (Object.prototype.hasOwnProperty.call(context_dictionaryToCreate, context_w)) {
        if (context_w.charCodeAt(0) < 256) {
          for (i = 0; i < context_numBits; i++) {
            context_data_val = (context_data_val << 1);
            if (context_data_position === bitsPerChar - 1) {
              context_data_position = 0;
              context_data.push(getCharFromInt(context_data_val));
              context_data_val = 0;
            } else {
              context_data_position++;
            }
          }
          value = context_w.charCodeAt(0);
          for (i = 0; i < 8; i++) {
            context_data_val = (context_data_val << 1) | (value & 1);
            if (context_data_position === bitsPerChar - 1) {
              context_data_position = 0;
              context_data.push(getCharFromInt(context_data_val));
              context_data_val = 0;
            } else {
              context_data_position++;
            }
            value = value >> 1;
          }
        } else {
          value = 1;
          for (i = 0; i < context_numBits; i++) {
            context_data_val = (context_data_val << 1) | value;
            if (context_data_position === bitsPerChar - 1) {
              context_data_position = 0;
              context_data.push(getCharFromInt(context_data_val));
              context_data_val = 0;
            } else {
              context_data_position++;
            }
            value = 0;
          }
          value = context_w.charCodeAt(0);
          for (i = 0; i < 16; i++) {
            context_data_val = (context_data_val << 1) | (value & 1);
            if (context_data_position === bitsPerChar - 1) {
              context_data_position = 0;
              context_data.push(getCharFromInt(context_data_val));
              context_data_val = 0;
            } else {
              context_data_position++;
            }
            value = value >> 1;
          }
        }
        context_enlargeIn--;
        if (context_enlargeIn === 0) {
          context_numBits++;
        }
        delete context_dictionaryToCreate[context_w];
      } else {
        value = context_dictionary[context_w];
        for (i = 0; i < context_numBits; i++) {
          context_data_val = (context_data_val << 1) | (value & 1);
          if (context_data_position === bitsPerChar - 1) {
            context_data_position = 0;
            context_data.push(getCharFromInt(context_data_val));
            context_data_val = 0;
          } else {
            context_data_position++;
          }
          value = value >> 1;
        }
      }
    }

    value = 2;
    for (i = 0; i < context_numBits; i++) {
      context_data_val = (context_data_val << 1) | (value & 1);
      if (context_data_position === bitsPerChar - 1) {
        context_data_position = 0;
        context_data.push(getCharFromInt(context_data_val));
        context_data_val = 0;
      } else {
        context_data_position++;
      }
      value = value >> 1;
    }

    while (true) {
      context_data_val = (context_data_val << 1);
      if (context_data_position === bitsPerChar - 1) {
        context_data.push(getCharFromInt(context_data_val));
        break;
      } else {
        context_data_position++;
      }
    }

    return context_data.join('');
  }

  return compress(input, 6, (a) => keyStr.charAt(a));
}

export const EmbedCodeModal = React.memo(function EmbedCodeModal({
  isOpen,
  onClose,
  files,
  projectName,
}: EmbedCodeModalProps) {
  const [options, setOptions] = useState<EmbedOptions>({
    showEditor: true,
    showPreview: true,
    showFileTree: false,
    theme: 'dark',
    height: '400px',
    width: '100%',
  });
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileEntries = Object.entries(files);
  const totalSize = fileEntries.reduce((sum, [, content]) => sum + content.length, 0);

  // Generate embed URL
  const embedUrl = useMemo(() => {
    try {
      const payload = JSON.stringify({
        name: projectName,
        files: files,
        options: {
          editor: options.showEditor,
          preview: options.showPreview,
          fileTree: options.showFileTree,
          theme: options.theme,
        },
      });

      // Check if payload is too large
      if (payload.length > 100000) {
        setError('Project is too large to embed. Consider using fewer files or CodeSandbox embed.');
        return null;
      }

      const compressed = compressToEncodedURIComponent(payload);
      const baseUrl = window.location.origin + window.location.pathname;
      return `${baseUrl}#embed=${compressed}`;
    } catch {
      setError('Failed to generate embed code');
      return null;
    }
  }, [files, projectName, options]);

  // Generate embed HTML code
  const embedCode = useMemo(() => {
    if (!embedUrl) return '';

    const attrs = [
      `src="${embedUrl}"`,
      `width="${options.width}"`,
      `height="${options.height}"`,
      'frameborder="0"',
      'allow="accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking"',
      'sandbox="allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"',
      `title="${projectName}"`,
    ];

    return `<iframe ${attrs.join('\n       ')}></iframe>`;
  }, [embedUrl, options, projectName]);

  // Check URL length on open
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setCopied(false);
    }
  }, [isOpen]);

  const handleCopy = useCallback(async () => {
    if (!embedCode) return;

    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  }, [embedCode]);

  const handleClose = useCallback(() => {
    setCopied(false);
    setError(null);
    onClose();
  }, [onClose]);

  const updateOption = useCallback(<K extends keyof EmbedOptions>(key: K, value: EmbedOptions[K]) => {
    setOptions(prev => ({ ...prev, [key]: value }));
  }, []);

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div
        className="modal embed-code-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '650px' }}
      >
        <div className="modal-header">
          <div className="modal-title">Embed Code</div>
          <button className="modal-close" onClick={handleClose}>
            x
          </button>
        </div>

        <div className="modal-body" style={{ padding: '16px' }}>
          <div style={{
            padding: '12px',
            background: 'var(--bg-secondary)',
            borderRadius: '4px',
            marginBottom: '16px',
          }}>
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
              Project: {projectName}
            </div>
            <div style={{ fontSize: '13px' }}>
              {fileEntries.length} file{fileEntries.length !== 1 ? 's' : ''} ({(totalSize / 1024).toFixed(1)} KB)
            </div>
          </div>

          {/* Options */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
              Display Options:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={options.showEditor}
                  onChange={(e) => updateOption('showEditor', e.target.checked)}
                />
                <span style={{ fontSize: '13px' }}>Show Editor</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={options.showPreview}
                  onChange={(e) => updateOption('showPreview', e.target.checked)}
                />
                <span style={{ fontSize: '13px' }}>Show Preview</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={options.showFileTree}
                  onChange={(e) => updateOption('showFileTree', e.target.checked)}
                />
                <span style={{ fontSize: '13px' }}>Show File Tree</span>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px' }}>Theme:</span>
                <select
                  value={options.theme}
                  onChange={(e) => updateOption('theme', e.target.value as 'dark' | 'light' | 'auto')}
                  style={{
                    padding: '4px 8px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    color: 'var(--text)',
                    fontSize: '12px',
                  }}
                >
                  <option value="dark">Dark</option>
                  <option value="light">Light</option>
                  <option value="auto">Auto</option>
                </select>
              </div>
            </div>
          </div>

          {/* Size Options */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                Width:
              </label>
              <input
                type="text"
                value={options.width}
                onChange={(e) => updateOption('width', e.target.value)}
                placeholder="100%"
                style={{
                  width: '100%',
                  padding: '8px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  color: 'var(--text)',
                  fontSize: '13px',
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                Height:
              </label>
              <input
                type="text"
                value={options.height}
                onChange={(e) => updateOption('height', e.target.value)}
                placeholder="400px"
                style={{
                  width: '100%',
                  padding: '8px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  color: 'var(--text)',
                  fontSize: '13px',
                }}
              />
            </div>
          </div>

          {error && (
            <div style={{
              padding: '12px',
              background: 'rgba(255, 100, 100, 0.1)',
              border: '1px solid var(--error)',
              borderRadius: '4px',
              marginBottom: '16px',
              color: 'var(--error)',
              fontSize: '13px',
            }}>
              {error}
            </div>
          )}

          {embedCode && !error && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                  Embed Code:
                </label>
                <div style={{
                  position: 'relative',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}>
                  <pre style={{
                    margin: 0,
                    padding: '12px',
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    maxHeight: '120px',
                    color: 'var(--text)',
                  }}>
                    {embedCode}
                  </pre>
                  <button
                    className="pill-btn"
                    onClick={handleCopy}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      fontSize: '11px',
                      padding: '4px 8px',
                      background: copied ? 'var(--success)' : 'var(--bg)',
                      color: copied ? 'white' : undefined,
                    }}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Preview */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                  Preview:
                </label>
                <div style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  padding: '12px',
                  textAlign: 'center',
                }}>
                  <div style={{
                    display: 'inline-block',
                    border: '2px dashed var(--border)',
                    borderRadius: '4px',
                    padding: '20px',
                    width: '200px',
                    height: '100px',
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                      {options.width} x {options.height}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                      Embed Preview
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          <div style={{
            padding: '12px',
            background: 'var(--bg-secondary)',
            borderRadius: '4px',
            fontSize: '11px',
            color: 'var(--muted)',
          }}>
            <strong>Note:</strong> The embed code includes all project files in the URL.
            For larger projects, consider using CodeSandbox or StackBlitz embed instead.
          </div>
        </div>

        <div className="modal-footer">
          <button className="pill-btn" onClick={handleClose}>
            Close
          </button>
          {embedCode && !error && (
            <button
              className="pill-btn"
              onClick={handleCopy}
              style={{
                background: copied ? 'var(--success)' : 'var(--accent)',
                color: 'white',
              }}
            >
              {copied ? 'Copied!' : 'Copy Embed Code'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

export default EmbedCodeModal;
